import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Wrench, Zap, Hammer, Paintbrush, Flower, Car, Globe,
  Home, Scissors, GraduationCap, PartyPopper, Laptop, Package, Heart,
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const iconMap = {
  // Legacy icon names (old flat categories)
  Sparkles, Wrench, Zap, Hammer, Paintbrush, Flower, Car, Refrigerator: Wrench,
  // New icon_slug values (10 new categories)
  Home, Scissors, GraduationCap, PartyPopper, Laptop, Package, Heart,
};

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/public/services-catalog`);
        const payload = response.data;
        const normalized = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.categories)
            ? payload.categories
            : [];
        setCategories(normalized);
      } catch (error) {
        console.error('Error fetching categories', error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const resolveName = (nameObj) => nameObj?.[i18n.language] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

  const Icon = ({ category }) => {
    const iconKey = category.icon_slug || category.icon || 'Sparkles';
    const IconComponent = iconMap[iconKey] || Sparkles;
    return <IconComponent className="w-12 h-12" />;
  };

  return (
    <div className="min-h-screen">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b" data-testid="landing-nav">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: 'hsl(215 100% 34%)' }}>
            Servicios
          </h1>
          <div className="flex items-center gap-6">
            <Button variant="ghost" onClick={() => navigate('/catalog')}>
              Browse Services
            </Button>
            <Button variant="ghost" onClick={() => navigate('/how-to-use')}>
              How to Use
            </Button>
            <Button variant="ghost" onClick={() => navigate('/contact')}>
              Contact
            </Button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('es')}
                className={i18n.language === 'es' ? 'bg-primary/10' : ''}
                data-testid="lang-es-button"
              >
                ES
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('en')}
                className={i18n.language === 'en' ? 'bg-primary/10' : ''}
                data-testid="lang-en-button"
              >
                EN
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => changeLanguage('fr')}
                className={i18n.language === 'fr' ? 'bg-primary/10' : ''}
                data-testid="lang-fr-button"
              >
                FR
              </Button>
            </div>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="rounded-full px-6" data-testid="landing-dashboard-button">
                Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate('/auth')} className="rounded-full px-6" data-testid="landing-get-started-button">
                {t('nav.login')}
              </Button>
            )}
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-4" style={{ background: 'linear-gradient(to bottom right, hsl(215 100% 95%), hsl(0 0% 100%), hsl(48 100% 95%))' }}>
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h1 
                className="text-5xl lg:text-6xl font-bold mb-6 text-balance"
                style={{ fontFamily: 'Outfit', color: 'hsl(215 25% 12%)' }}
                data-testid="hero-title"
              >
                {t('home.hero_title')}
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                {t('home.hero_subtitle')}
              </p>
              <Button
                size="lg"
                onClick={() => navigate('/auth')}
                className="rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ background: 'hsl(48 100% 50%)', color: 'hsl(215 25% 12%)' }}
                data-testid="hero-get-started-button"
              >
                {t('home.get_started')}
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="space-y-4">
                <img src="https://images.pexels.com/photos/6196239/pexels-photo-6196239.jpeg" alt="Cleaning" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
                <img src="https://images.pexels.com/photos/4315571/pexels-photo-4315571.jpeg" alt="Mechanic" className="rounded-2xl shadow-lg w-full h-64 object-cover" />
              </div>
              <div className="pt-12 space-y-4">
                <img src="https://images.pexels.com/photos/5691588/pexels-photo-5691588.jpeg" alt="Electrician" className="rounded-2xl shadow-lg w-full h-64 object-cover" />
                <div className="rounded-2xl bg-white p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2" style={{ color: 'hsl(120 100% 30%)' }}>5000+</div>
                  <p className="text-sm text-muted-foreground">Profesionales Verificados</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Outfit' }}>
            {t('home.how_it_works')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <Card className="p-8 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4 p-0">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'hsl(215 100% 95%)' }}>
                  <Globe className="w-8 h-8" style={{ color: 'hsl(215 100% 34%)' }} />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>{t('home.client_title')}</h3>
                <p className="text-muted-foreground">{t('home.client_desc')}</p>
              </CardContent>
            </Card>
            <Card className="p-8 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4 p-0">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'hsl(48 100% 95%)' }}>
                  <Wrench className="w-8 h-8" style={{ color: 'hsl(48 100% 40%)' }} />
                </div>
                <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>{t('home.provider_title')}</h3>
                <p className="text-muted-foreground">{t('home.provider_desc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 px-4" style={{ background: 'hsl(60 5% 98%)' }}>
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Outfit' }}>
            {t('home.categories_title')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className="p-6 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`category-card-${category.id}`}
                  onClick={() => navigate(`/catalog/${category.slug || category.id}`)}
                >
                  <CardContent className="flex flex-col items-center text-center space-y-4 p-0">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: 'hsl(215 100% 95%)' }}>
                      <Icon category={category} />
                    </div>
                    <h3 className="font-semibold text-lg">{resolveName(category.name)}</h3>
                    {category.provider_count && (
                      <Badge variant="secondary">{category.provider_count} providers</Badge>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-slate-400">© 2025 Servicios. El Salvador.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
